/**
 * Thumbnail of a Knowledge Area image document as an object URL
 * (`GET /documents/{id}/thumbnail`, cdb_tqs#1515).
 *
 * The endpoint wants a Bearer token, so an `<img src>` cannot point at it; the
 * JPEG is fetched through the API client and handed out as a `blob:` URL.
 * One fetch per document and size, shared by every card, chip and table row
 * that shows it. When the last of them is gone, the URL is released after a
 * short grace period — long enough that collapsing and re-opening the sources
 * does not refetch, short enough that a long chat does not hold every picture.
 * A repeat fetch is cheap anyway: the response is `private, max-age=86400`.
 *
 * No thumbnail (not an image, older backend, network error) resolves to null;
 * callers show their icon then.
 */
import { useEffect, useState } from 'react'
import { cdbTqsApiService } from '@/services/cdb-tqs-api-service'
import { logError } from '@/lib/logger'

export type ThumbnailSize = 160 | 320 | 640

/** How long an unused thumbnail stays cached before its URL is revoked. */
export const THUMBNAIL_RELEASE_DELAY_MS = 30_000

interface Entry {
  promise: Promise<string | null>
  /** undefined while loading. */
  url: string | null | undefined
  refs: number
  controller: AbortController
  releaseTimer: ReturnType<typeof setTimeout> | null
}

const cache = new Map<string, Entry>()

function keyOf(documentId: string, size: ThumbnailSize): string {
  return `${documentId}:${size}`
}

function load(documentId: string, size: ThumbnailSize, controller: AbortController): Promise<string | null> {
  return cdbTqsApiService
    .getDocumentThumbnail(documentId, size, controller.signal)
    .then((blob) => (blob ? URL.createObjectURL(blob) : null))
    .catch((error: unknown) => {
      if (!controller.signal.aborted) {
        logError(error as Error, { context: 'useDocumentThumbnail', documentId, size })
      }
      return null
    })
}

function acquire(documentId: string, size: ThumbnailSize): Entry {
  const key = keyOf(documentId, size)
  let entry = cache.get(key)
  if (!entry) {
    const controller = new AbortController()
    const created: Entry = {
      promise: load(documentId, size, controller),
      url: undefined,
      refs: 0,
      controller,
      releaseTimer: null,
    }
    void created.promise.then((url) => {
      created.url = url
    })
    cache.set(key, created)
    entry = created
  }
  if (entry.releaseTimer !== null) {
    clearTimeout(entry.releaseTimer)
    entry.releaseTimer = null
  }
  entry.refs += 1
  return entry
}

function evict(key: string): void {
  const entry = cache.get(key)
  if (!entry || entry.refs > 0) return
  cache.delete(key)
  entry.controller.abort()
  void entry.promise.then((url) => {
    if (url) URL.revokeObjectURL(url)
  })
}

function release(documentId: string, size: ThumbnailSize): void {
  const key = keyOf(documentId, size)
  const entry = cache.get(key)
  if (!entry) return
  entry.refs = Math.max(0, entry.refs - 1)
  if (entry.refs === 0 && entry.releaseTimer === null) {
    entry.releaseTimer = setTimeout(() => {
      entry.releaseTimer = null
      evict(key)
    }, THUMBNAIL_RELEASE_DELAY_MS)
  }
}

/** Drops every cached thumbnail at once and revokes its URL (test isolation). */
export function clearThumbnailCache(): void {
  for (const [key, entry] of cache) {
    if (entry.releaseTimer !== null) clearTimeout(entry.releaseTimer)
    entry.refs = 0
    evict(key)
  }
}

/**
 * Object URL of the document's thumbnail, or null (loading, none, disabled).
 * Pass `enabled: false` until the picture is on screen.
 */
export function useDocumentThumbnail(
  documentId: string | null | undefined,
  size: ThumbnailSize,
  enabled: boolean,
): string | null {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled || !documentId) {
      setUrl(null)
      return
    }
    let alive = true
    const entry = acquire(documentId, size)
    if (entry.url !== undefined) {
      setUrl(entry.url)
    } else {
      setUrl(null)
      void entry.promise.then((result) => {
        if (alive) setUrl(result)
      })
    }
    return () => {
      alive = false
      release(documentId, size)
    }
  }, [documentId, size, enabled])

  return url
}
